import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormInput, FormSelect, FormSwitch } from '@/components/forms'
import { TransferFlowSection } from '../TransferFlowSection'
import { WorkingHoursSection } from '../WorkingHoursSection'
import { PhoneNumberInput } from '../PhoneNumberInput'

/**
 * Telephony Tab - Phone and call settings
 *
 * Includes:
 * - Phone number (API-based autocomplete with pencil unlock)
 * - Fallback number
 * - Recording mode and notice
 * - Transfer flows
 * - Answer schedule
 */
export function TelephonyTab() {
  const { t } = useTranslation()
  const { control, getValues } = useFormContext()
  const recordingMode = useWatch({ control, name: 'config.recording_mode' })
  const assistantId = getValues('id')

  return (
    <div className="space-y-8">

      {/* Phone Number Section */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-6">
          {/* Phone Number */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.telephony.phone_number')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.telephony.phone_number_helper')}
            </p>
            <PhoneNumberInput assistantId={assistantId} />
          </div>

          {/* Fallback Number */}
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.telephony.fallback_number')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.telephony.fallback_number_helper')}
            </p>
            <FormInput
              name="fallback_number"
              control={control}
              placeholder="+31612345678"
            />
          </div>
        </div>
      </div>

      {/* Recording Settings */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="space-y-6">
          <div>
            <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('resources.assistants.telephony.recording_mode')}
            </h3>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.telephony.recording_mode_description')}
            </p>
            <FormSelect
              name="config.recording_mode"
              control={control}
              options={[
                { value: 'full', label: t('resources.assistants.telephony.recording_mode_full') },
                { value: 'ai_only', label: t('resources.assistants.telephony.recording_mode_ai_only') },
                { value: 'none', label: t('resources.assistants.telephony.recording_mode_none') },
              ]}
            />
          </div>

          {/* Recording Notice - only show if recording is enabled */}
          {(recordingMode === 'full' || recordingMode === 'ai_only') && (
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('resources.assistants.telephony.recording_notice')}
              </h3>
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.telephony.recording_notice_helper')}
              </p>
              <FormSwitch name="config.recording_notice_enabled" control={control} />
            </div>
          )}
        </div>
      </div>

      {/* Transfer Flows */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <TransferFlowSection />
      </div>

      {/* Answer Schedule */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <WorkingHoursSection />
      </div>
    </div>
  )
}
